import { Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useState, type MouseEvent } from 'react';
import LeadNewStatus from './LeadNewStatus';
import { useSettings } from 'context/SettingsContext';
import { DateTime as DT } from 'luxon';
import vconst from 'utils/helpers/structs/vconst';
import type { LeadCardStatusProps } from '../types';
import { useAuth } from 'context/AuthContext';
import { hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import { useNeedPay } from 'modules/admin';
import LeadApprovedProposal from '../LeadApprovedProposal';
import { buildNewObjectFromLead } from '../helpers/proposalAmountToObjectPrice';

const LeadCardStatus = ({
    setNewEvent,
    setNewObject,
    lead,
    statuses,
    acceptedGeneration = null,
    acceptedLoading = false,
    hasLeadProposal = false,
    onOpenProposal = null,
}: LeadCardStatusProps) => {
    const { t } = useTranslation(['Leads']);
    const { localSettings } = useSettings();
    const { auth } = useAuth();
    const { needPay } = useNeedPay();
    const canCreateObject = hasUserAccess(auth?.user, 'objectCreate');
    const rate = Number(localSettings?.RATE?.current ?? 1) || 1;
    const timeline = statuses || [];
    const hasTimeline = timeline.length > 0;
    const [historyCollapsed, setHistoryCollapsed] = useState(false);

    const handleClickStatus = (_e: MouseEvent, statusID: number) => {
        if (needPay()) return;
        if (statusID === 400)
            setNewObject(buildNewObjectFromLead(lead, acceptedGeneration, rate));
        else
            setNewEvent({
                leadId: lead.ID,
                statusID: statusID,
                leadPendingDt:
                    statusID === vconst.leadPostponed ? Number(lead.leadPendingDt) || 0 : 0,
            });
    };

    const handleToggleHistory = () => {
        setHistoryCollapsed((prev) => !prev);
    };

    const statusActions =
        lead.leadStatus !== vconst.leadAccept ? (
            <div className="lead-status-card__actions">
                <LeadNewStatus
                    sm={false}
                    title={t('leadStatus.newStatus')}
                    onClick={handleClickStatus}
                    showAcceptStatus={canCreateObject}
                />
            </div>
        ) : null;

    return (
        <Card className="lead-status-card">
            <Card.Body className="lead-status-card__body p-0">
                <div className="lead-status-card__header">
                    <h5 className="lead-status-card__title">
                        <i className="bi bi-clock-history lead-status-card__title-icon" aria-hidden="true"></i>
                        {t('leadStatus.title')}
                    </h5>
                    {hasTimeline ? (
                        <button
                            type="button"
                            className="btn lead-status-card__collapse-btn"
                            onClick={handleToggleHistory}
                            title={
                                historyCollapsed
                                    ? t('leadStatus.expandHistory')
                                    : t('leadStatus.collapseHistory')
                            }
                            aria-label={
                                historyCollapsed
                                    ? t('leadStatus.expandHistory')
                                    : t('leadStatus.collapseHistory')
                            }
                            aria-expanded={!historyCollapsed}
                        >
                            <i
                                className={`bi ${historyCollapsed ? 'bi-chevron-down' : 'bi-chevron-up'}`}
                                aria-hidden="true"
                            ></i>
                        </button>
                    ) : null}
                </div>

                <div
                    className={`lead-status-card__content${
                        historyCollapsed ? ' is-history-collapsed' : ''
                    }`}
                >
                    {historyCollapsed ? statusActions : null}

                    {!historyCollapsed && hasTimeline ? (
                        <ul className="lead-status-timeline list-unstyled mb-0">
                            {timeline.map((status) => {
                                const dirStat = localSettings?.DIRS?.LeadStatus?.find(
                                    (s: any) => s.ID == status.eventStatus,
                                );
                                const dt = DT.fromMillis(status.eventCreated).toLocaleString(
                                    DT.DATETIME_SHORT,
                                );
                                const style = dirStat?.statusJson?.statusStyle || 'danger';
                                return (
                                    <li key={status?.eventCreated} className="lead-status-timeline__item">
                                        <i className={`lead-status-timeline__dot bg-${style}`}></i>
                                        <a
                                            className="lead-status-timeline__link"
                                            href={`#event-${status.eventCreated}`}
                                        >
                                            <span className="lead-status-timeline__label">
                                                {t(`status.${status.eventStatus}`)}
                                            </span>
                                            <span className="lead-status-timeline__date">{dt}</span>
                                        </a>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : null}

                    {!historyCollapsed ? statusActions : null}
                </div>

                <LeadApprovedProposal
                    generation={acceptedGeneration}
                    loading={acceptedLoading}
                    hasLeadProposal={hasLeadProposal}
                    onOpenProposal={onOpenProposal}
                />
            </Card.Body>
        </Card>
    );
};

export default LeadCardStatus;
